import { useCallback, useEffect, useRef, useState } from 'react';
import type { TripRoute } from '../types/trip';

// 🚨 Ajustado el 2026-09-18 (segunda vuelta, a pedido del dueño): "1x" es
// tiempo real — 1 segundo real = 1 segundo del recorrido —, no una
// compresión del rango completo a una duración fija. Antes el rango entero
// (20 min o 17 horas, daba igual) se estiraba a los mismos 90s: dos
// recorridos de duración muy distinta se veían igual de "rápidos" o
// "lentos" sin relación con el tiempo real. Las demás velocidades son
// múltiplos directos de ese tiempo real, como el control de velocidad de
// cualquier reproductor de video.
const SPEED_OPTIONS = [0.25, 0.5, 1, 2, 4];
const DEFAULT_SPEED_INDEX = 2; // 1x
// `setInterval`, no `requestAnimationFrame`: esto es una animación lenta de
// panel (no un juego a 60fps) y `rAF` se pausa cuando el navegador considera
// la pestaña "ocluida" —aunque `document.visibilityState` siga en
// "visible"— lo que dejaría la reproducción congelada sin ningún error.
// 50ms alcanza de sobra para que se vea fluida.
const TICK_MS = 50;

/**
 * FE-26 — motor de la reproducción animada del histórico (FE-25). Solo
 * avanza un reloj (`currentMs`) entre el primer y el último punto del
 * recorrido; quién dibuja el marcador interpolado es
 * `interpolateRoutePosition` (`utils/vehicleHistory.ts`).
 */
export function useRoutePlayback(route: TripRoute | null) {
  const points = route?.points ?? [];
  const startMs = points.length > 0 ? Date.parse(points[0].occurredAt) : 0;
  const endMs = points.length > 0 ? Date.parse(points[points.length - 1].occurredAt) : 0;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMs, setCurrentMs] = useState(startMs);
  const [speedIndex, setSpeedIndex] = useState(DEFAULT_SPEED_INDEX);

  const lastTickRef = useRef<number | null>(null);

  // Nuevo recorrido (nueva consulta o "Limpiar"): vuelve al inicio y para.
  useEffect(() => {
    setIsPlaying(false);
    setCurrentMs(startMs);
    lastTickRef.current = null;
  }, [route, startMs]);

  useEffect(() => {
    if (!isPlaying || endMs <= startMs) return;

    lastTickRef.current = Date.now();

    const id = setInterval(() => {
      const now = Date.now();
      const deltaWallMs = now - (lastTickRef.current ?? now);
      lastTickRef.current = now;

      setCurrentMs(prev => {
        const next = prev + deltaWallMs * SPEED_OPTIONS[speedIndex];
        if (next >= endMs) {
          setIsPlaying(false);
          return endMs;
        }
        return next;
      });
    }, TICK_MS);

    return () => {
      clearInterval(id);
      lastTickRef.current = null;
    };
  }, [isPlaying, speedIndex, startMs, endMs]);

  const play = useCallback(() => {
    setCurrentMs(prev => (prev >= endMs ? startMs : prev));
    setIsPlaying(true);
  }, [startMs, endMs]);

  const pause = useCallback(() => setIsPlaying(false), []);

  const seek = useCallback((ms: number) => {
    setCurrentMs(Math.min(Math.max(ms, startMs), endMs));
  }, [startMs, endMs]);

  const cycleSpeed = useCallback(() => {
    setSpeedIndex(i => (i + 1) % SPEED_OPTIONS.length);
  }, []);

  return {
    isPlaying,
    currentMs,
    startMs,
    endMs,
    speedLabel: `${SPEED_OPTIONS[speedIndex]}x`,
    canPlay: points.length >= 2,
    play,
    pause,
    seek,
    cycleSpeed,
  };
}
